import React, { useEffect, useState } from "react";
import { Input, Button, message } from "antd";
import { SettingOutlined } from "@ant-design/icons";
import DashboardWidget from "../../../components/dashboard/DashboardWidget";
import PageHeader from "../../../components/generic/PageHeader";
import DropdownSelect from "../../../components/generic/DropdownSelect";
import {
  getDataSubTypeByType,
  saveSettings,
} from "../../../service/admin.service";
import { getCurrentDateTimeInFormat } from "../../../helper/date_helper";
import "../../../styles/db.css";

const SECTIONS = [
  {
    key: "DocumentStorage",
    title: "Document Storage",
    subtitle: "Where uploaded control documents are stored",
    accent: "#185FA5",
    fields: [
      {
        name: "provider",
        label: "Storage Provider",
        type: "select",
        options: [
          { label: "AWS S3", value: "S3" },
          { label: "Server (filesystem)", value: "SERVER" },
        ],
        default: "S3",
      },
      {
        name: "bucket",
        label: "S3 Bucket Name",
        type: "text",
        placeholder: "e.g. arc-fsd-bucket",
        default: "arc-fsd-bucket",
        showWhen: (v) => v.provider === "S3",
      },
      {
        name: "serverPath",
        label: "Server Folder Path",
        type: "text",
        placeholder: "e.g. C:\\ARC_APP\\control-docs",
        default: "",
        showWhen: (v) => v.provider === "SERVER",
      },
    ],
    validate: (v) => {
      if (v.provider === "S3" && !String(v.bucket || "").trim())
        return "S3 bucket name is required";
      if (v.provider === "SERVER" && !String(v.serverPath || "").trim())
        return "Server folder path is required";
      return null;
    },
  },
];

function SectionCard({ section }) {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const applyDefaults = (map) => {
      const init = {};
      section.fields.forEach((f) => {
        init[f.name] =
          map[f.name] !== undefined && map[f.name] !== null
            ? map[f.name]
            : f.default;
      });
      setValues(init);
    };
    getDataSubTypeByType("Settings", section.key)
      .then((res) => {
        const map = {};
        (res.data || []).forEach((row) => {
          map[row.name] = row.comment;
        });
        applyDefaults(map);
      })
      .catch(() => applyDefaults({}))
      .finally(() => setLoading(false));
  }, [section]);

  const setField = (name, val) => setValues((p) => ({ ...p, [name]: val }));

  const onSave = () => {
    const err = section.validate ? section.validate(values) : null;
    if (err) {
      message.error(err);
      return;
    }
    setSaving(true);
    const now = getCurrentDateTimeInFormat();
    // Persist only the fields relevant to the current selection.
    const rows = section.fields
      .filter((f) => !f.showWhen || f.showWhen(values))
      .map((f) => ({
        dataType: "Settings",
        dataSubType: section.key,
        name: f.name,
        description: f.label,
        comment: values[f.name] != null ? String(values[f.name]) : "",
        createdDate: now,
      }));
    saveSettings(rows)
      .then(() => message.success(`${section.title} settings saved`))
      .catch(() => message.error("Failed to save settings"))
      .finally(() => setSaving(false));
  };

  return (
    <DashboardWidget
      title={section.title}
      subtitle={section.subtitle}
      accent={section.accent}
      loading={loading}
    >
      <div
        style={{
          maxWidth: 520,
          display: "flex",
          flexDirection: "column",
          gap: 14,
          paddingTop: 4,
        }}
      >
        {section.fields.map((f) => {
          if (f.showWhen && !f.showWhen(values)) return null;
          return (
            <div key={f.name}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#374151",
                  marginBottom: 6,
                }}
              >
                {f.label}
              </div>
              {f.type === "select" ? (
                <DropdownSelect
                  value={values[f.name]}
                  options={f.options}
                  labelKey="label"
                  valueKey="value"
                  onSelect={(val) => setField(f.name, val)}
                />
              ) : (
                <Input
                  value={values[f.name]}
                  placeholder={f.placeholder}
                  onChange={(e) => setField(f.name, e.target.value)}
                />
              )}
            </div>
          );
        })}
        <div>
          <Button
            type="primary"
            className="db-add"
            loading={saving}
            onClick={onSave}
          >
            Save
          </Button>
        </div>
      </div>
    </DashboardWidget>
  );
}

function Settings() {
  return (
    <div className="db-container">
      <PageHeader
        backTo="/admin"
        icon={<SettingOutlined />}
        title="Settings"
        tooltip="Application configuration"
      />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
          maxWidth: 760,
        }}
      >
        {SECTIONS.map((s) => (
          <SectionCard key={s.key} section={s} />
        ))}
      </div>
    </div>
  );
}

export default Settings;
